import { useEffect, useState } from "react";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import UserAvatar from "./management/UserAvatar";

export const shareDialogSearchSx = {
    mb: 1.25,
    "& .MuiOutlinedInput-root": {
        borderRadius: 2,
    },
};

export function ShareDialogPanelHeading({ count, title }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 1.25,
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {count}
            </Typography>
        </Box>
    );
}

export function ShareDialogUserListLoading({ label }) {
    return (
        <Box
            role="status"
            sx={{
                height: { md: 390 },
                minHeight: 150,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                textAlign: "center",
                px: 2,
            }}
        >
            <CircularProgress size={26} />
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );
}

export function ShareDialogGroupIcon({ available = false }) {
    return (
        <Box
            sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                color: available ? "text.secondary" : "primary.main",
                backgroundColor: available
                    ? "rgba(20, 35, 25, 0.06)"
                    : "rgba(0, 149, 61, 0.09)",
            }}
        >
            <GroupsOutlinedIcon fontSize="small" />
        </Box>
    );
}

export function ShareDialogAvailableGroupRow({
    divided = false,
    memberCount,
    name,
    onClick,
}) {
    return (
        <>
            <ListItemButton
                onClick={onClick}
                sx={{ gap: 1, px: 1.25, py: 0.85 }}
            >
                <ShareDialogGroupIcon available />
                <ListItemText
                    primary={name}
                    secondary={`${memberCount} ${
                        memberCount === 1 ? "User" : "Users"
                    }`}
                    primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 800,
                        noWrap: true,
                    }}
                    secondaryTypographyProps={{
                        variant: "caption",
                        noWrap: true,
                    }}
                />
                <AddCircleOutlineIcon
                    fontSize="small"
                    sx={{ color: "primary.main" }}
                />
            </ListItemButton>
            {divided && <Divider />}
        </>
    );
}

const compareUsers = (first, second) => {
    const firstName = (first.lastName || first.fullName || "").toLowerCase();
    const secondName = (second.lastName || second.fullName || "").toLowerCase();

    return firstName.localeCompare(secondName);
};

// Group membership is stored as keys only, so pair each one back up with the
// user it refers to. Members with no matching user are still listed, so a group
// never appears to have fewer people in it than its own member count says.
export function resolveGroupMembers(group, users) {
    if (!group) return [];

    return (group.users || [])
        .map(
            (member) =>
                users.find((user) => user.key === member.key) || {
                    key: member.key,
                    fullName: "User unavailable",
                },
        )
        .sort(compareUsers);
}

// Everyone the selected groups bring in, and which of those groups brought
// each of them. Users in several selected groups appear once.
export function buildGroupCoverage({ getGroupName, selectedGroups, users }) {
    const groupNamesByUserKey = new Map();
    const memberKeys = new Set();

    (selectedGroups || []).forEach((group) => {
        const name = getGroupName(group);

        (group.users || []).forEach((member) => {
            memberKeys.add(member.key);

            const names = groupNamesByUserKey.get(member.key);
            if (names) {
                names.push(name);
            } else {
                groupNamesByUserKey.set(member.key, [name]);
            }
        });
    });

    const members = Array.from(memberKeys)
        .map((key) => users.find((user) => user.key === key))
        .filter(Boolean)
        .sort(compareUsers);

    return { groupNamesByUserKey, memberKeys, members };
}

export function shareDialogGroupAccessSummary(groupNames = []) {
    if (groupNames.length === 0) return "Via a group";
    if (groupNames.length === 1) return `Via ${groupNames[0]}`;

    return `Via ${groupNames[0]} +${groupNames.length - 1} more`;
}

function groupAccessTitle(user, groupNames = []) {
    if (groupNames.length === 0) {
        return `${user.fullName} has access through a selected group. Remove that group to change this.`;
    }

    return `${user.fullName} has access through ${groupNames.join(
        ", ",
    )}. Remove the ${
        groupNames.length === 1 ? "group" : "groups"
    } to change this.`;
}

export function ShareDialogUserSectionHeader({ count, icon, label }) {
    return (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.8,
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.subtle",
            }}
        >
            {icon}
            <Typography
                variant="caption"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 800,
                    letterSpacing: "0.025em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
                {count}
            </Typography>
        </Box>
    );
}

// A user who is only here because one of the selected groups covers them, so
// the row says where the access came from and offers no way to remove it
export function ShareDialogGroupUserRow({
    divided = false,
    groupNames = [],
    roomy = false,
    user,
}) {
    return (
        <>
            <ListItem
                title={groupAccessTitle(user, groupNames)}
                sx={{
                    gap: 1,
                    px: roomy ? { xs: 1.5, sm: 2 } : 1.25,
                    py: roomy ? 1.5 : 0.65,
                    opacity: 0.62,
                }}
                secondaryAction={
                    <IconButton
                        aria-label={`${user.fullName} cannot be removed individually`}
                        disabled
                        size="small"
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                }
            >
                <ListItemAvatar>
                    <UserAvatar
                        userOrHub={user}
                        path="users"
                        variant="circular"
                        size={roomy ? "medium" : "small"}
                    />
                </ListItemAvatar>
                <ListItemText
                    primary={user.fullName}
                    secondary={shareDialogGroupAccessSummary(groupNames)}
                    primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 800,
                        noWrap: true,
                    }}
                    secondaryTypographyProps={{
                        variant: "caption",
                        noWrap: true,
                    }}
                />
            </ListItem>
            {divided && <Divider />}
        </>
    );
}

export function ShareDialogGroupMembersDialog({
    breadcrumb,
    groupName,
    members = [],
    onClose,
    open,
}) {
    const [filter, setFilter] = useState("");

    useEffect(() => {
        setFilter("");
    }, [open, groupName]);

    const filteredMembers = members.filter((member) =>
        (member.fullName || "")
            .toLowerCase()
            .includes(filter.trim().toLowerCase()),
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            aria-labelledby="share-dialog-group-members-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "88vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <ShareDialogGroupIcon />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="share-dialog-group-members-title"
                        variant="h6"
                        component="h2"
                        noWrap
                        sx={{ lineHeight: 1.2 }}
                    >
                        {groupName}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={breadcrumb}
                    >
                        {breadcrumb}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {`${members.length} ${
                            members.length === 1 ? "user" : "users"
                        }`}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close the group member list"
                    onClick={onClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <TextField
                    label="Search users in this group"
                    size="small"
                    fullWidth
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    sx={{
                        ...shareDialogSearchSx,
                        backgroundColor: "background.paper",
                    }}
                />
                {filteredMembers.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: 150,
                            display: "grid",
                            placeItems: "center",
                            textAlign: "center",
                            px: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {members.length === 0
                                ? "This group has no users in it."
                                : "No users in this group match this search."}
                        </Typography>
                    </Box>
                ) : (
                    <List
                        disablePadding
                        sx={{
                            maxHeight: 360,
                            overflowY: "auto",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                        }}
                    >
                        {filteredMembers.map((member, index) => (
                            <Box key={member.key}>
                                <ListItem sx={{ gap: 1, px: 1.25, py: 0.65 }}>
                                    <ListItemAvatar>
                                        <UserAvatar
                                            userOrHub={member}
                                            path="users"
                                            variant="circular"
                                            size="small"
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={member.fullName}
                                        secondary={member.customRoleName}
                                        primaryTypographyProps={{
                                            variant: "body2",
                                            fontWeight: 800,
                                            noWrap: true,
                                        }}
                                        secondaryTypographyProps={{
                                            variant: "caption",
                                            noWrap: true,
                                        }}
                                    />
                                </ListItem>
                                {index < filteredMembers.length - 1 && (
                                    <Divider />
                                )}
                            </Box>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button color="inherit" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ShareDialogGroupHeader({ breadcrumb }) {
    return (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.8,
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.subtle",
            }}
        >
            <AccountTreeOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            <Typography
                variant="caption"
                title={breadcrumb}
                sx={{
                    minWidth: 0,
                    fontWeight: 800,
                    letterSpacing: "0.025em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    direction: "rtl",
                    textAlign: "left",
                    unicodeBidi: "plaintext",
                }}
            >
                {breadcrumb}
            </Typography>
        </Box>
    );
}
